/**
 * The audience's RudderStack mirror state, on the artifact page — a callout only when there is something
 * to say.
 *
 * A push to the mirrored `sql-audience` rETL source never fails the save (ACT2-506 spec, §Error handling:
 * "Save succeeds; `rs_push_error` recorded; surfaced on the audience artifact"), so a failure is otherwise
 * invisible: it surfaces here in an error callout with the stored one-line reason. Behind-the-definition is
 * a warning callout carrying the only "Sync now" action there is.
 *
 * A HEALTHY mirror renders nothing (ACT2-600). A green "Synced to RudderStack" band sat above the
 * definition on every visit, announcing that the thing that was supposed to happen had happened — it cost
 * the reader attention on every load and told them nothing they could act on. Say nothing on the happy
 * path; speak when the sync needs a person. The `synced` state still EXISTS in `RsSyncState` — the retry
 * gating and the save toast both read it — it simply draws no band. An audience with no mirror at all
 * (pre-feature rows) renders nothing either, exactly as before. A demo workspace is `synced`, not
 * no-mirror — it gets a synthetic local mirror — so it follows the healthy path above.
 *
 * `onRetry` re-pushes the mirror (a re-save with `status: 'saved'`, which the service re-pushes) — the retry
 * the draft/active save model otherwise strands, since a clean saved audience has no header Save button.
 * That re-save is gated by the server on a fresh count, so `retryDisabledReason` disables the action (with
 * the reason) when the size needs (re)calculating first.
 */
import { RefreshCw } from 'lucide-react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';

import type { RsSyncState } from '../lib/audience-view';

export function AudienceSyncStatus({
  state,
  onRetry,
  retrying,
  retryDisabledReason,
}: {
  state: RsSyncState;
  /** Re-push the mirror (a re-save with `status: 'saved'`). */
  onRetry: () => void;
  retrying: boolean;
  /** When set, the retry is disabled and this is its tooltip reason — the re-save needs a fresh count. */
  retryDisabledReason?: string;
}): React.ReactElement | null {
  // Nothing to report: no mirror at all, or a mirror that is doing its job. See the docblock.
  if (state.status === 'none' || state.status === 'synced') return null;

  // `keepMounted`: `retryDisabledReason` is transient (it appears/clears as the count goes stale/fresh), so
  // the `show` flip would otherwise swap the tooltip tree for a bare child and remount the Button, dropping
  // a keyboard user's focus mid-interaction — the same class the Show-SQL fix addresses. Paired with the
  // ActionTooltip `open` fix so it stays controlled across the flip rather than warning.
  const retryButton = (label: string): React.ReactElement => (
    <ActionTooltip show={!!retryDisabledReason} message={retryDisabledReason ?? ''} keepMounted>
      <Button
        variant="secondary"
        size="md"
        Icon={<RefreshCw />}
        onClick={onRetry}
        loading={retrying}
        disabled={retrying || !!retryDisabledReason}
      >
        {label}
      </Button>
    </ActionTooltip>
  );

  if (state.status === 'error') {
    return (
      <Callout variant="error" className="mb-4">
        <p className="font-medium">Sync to RudderStack failed</p>
        <p className="mt-0.5 wrap-break-word">{state.error}</p>
        <p className="mt-0.5">The audience itself saved.</p>
        <div className="mt-2">{retryButton('Retry sync')}</div>
      </Callout>
    );
  }

  // ACT2-1286: no retry here — a re-save would skip its push again. Confirming the repair pushes, so
  // there is no separate Sync changes action.
  if (state.status === 'needs-repair') {
    return (
      <Callout variant="warning" className="mb-4">
        <p className="font-medium">Not synced to RudderStack yet</p>
        <p className="mt-0.5">The last save waits for the warehouse account repair above.</p>
      </Callout>
    );
  }

  if (state.status === 'out-of-sync') {
    return (
      <Callout variant="warning" className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <p className="font-medium">RudderStack copy is out of date</p>
          {retryButton('Sync now')}
        </div>
      </Callout>
    );
  }

  // Exhaustive by construction. Before ACT2-600 the tail of this function was the passive success
  // callout, so an unhandled `RsSyncState` arm degraded to a harmless green band; the warning is the
  // last branch now, and letting a new arm FALL INTO it would put "RudderStack copy is out of date" and
  // a button that fires a real re-save in front of a state nobody wrote it for. `satisfies never` turns
  // the next arm into a compile error instead.
  state satisfies never;
  return null;
}
